/**
 * TaskActivitySection — read-only audit-log feed for a task (T2.4.1).
 *
 * Mounted inside the existing ``<Section title="Aktivität">`` slot on
 * AdminTaskDetailPage. Mirrors TaskCommentsSection's structure
 * (loading / error / empty / list) but with two semantic differences:
 *
 *   - **Newest first** (operations-log convention — Linear/Jira style).
 *     Comments are oldest-first (conversation thread). Two different
 *     mental models, kept visually distinct.
 *   - **Refreshes on parent's signal.** A ``refreshKey`` prop bumped
 *     by the parent (currently when a comment is added) triggers a
 *     refetch so the freshly-written "comment_added" audit row shows
 *     up without a page reload.
 *
 * Race guard: rapid ``refreshKey`` changes can let an older fetch
 * resolve after a newer one. ``requestIdRef`` lets the latest fetch
 * win — older promises bail out of ``.then`` / ``.catch`` / ``.finally``
 * before mutating any state.
 *
 * Author display: ``actor_user_full_name`` → ``actor_user_email`` →
 * "Unbekannt". Never a raw UUID (same contract as comments).
 *
 * Unknown action strings: rendered verbatim and logged once via
 * ``console.warn`` so missing label mappings surface during dev.
 *
 * Scope: read-only — no edit, no delete, no diff rendering. Adding
 * inline diff display is a follow-up; the schema already withholds
 * ``old_values`` / ``new_values`` from the wire payload.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { fetchAdminTaskActivity } from "../../../api/adminData";
import { formatDateTime } from "../../../utils/datetime";


// ──────────────────────────────────────────────────────────────────────
// Action label map — keys are the EXACT strings the backend writes
// via ``create_audit_log(action=...)`` in:
//   - app/services/task_service.py:   "create", "update", "complete",
//                                     "reopen", "delete"
//   - app/api/v1/routes_admin_tasks.py (admin_create_task_comment):
//                                     "comment_added"
// Anything else falls through to ``formatAction`` and renders raw
// + logs a one-shot warning via WARNED_ACTIONS.
// ──────────────────────────────────────────────────────────────────────

const ACTION_LABELS = {
  create: "Aufgabe erstellt",
  update: "Aufgabe bearbeitet",
  complete: "Als erledigt markiert",
  reopen: "Wieder geöffnet",
  delete: "Aufgabe gelöscht",
  comment_added: "Kommentar hinzugefügt",
  comment_edited: "Kommentar bearbeitet", // T2.4.2
  comment_deleted: "Kommentar gelöscht", // T2.4.2
  // T2.4.3 — strict workflow transitions. ``start`` is written for
  // both ``todo → in_progress`` AND ``blocked → in_progress`` (single
  // audit row per transition; the prior-blocked state is recoverable
  // from the preceding ``block`` row in the same timeline).
  start: "Bearbeitung gestartet",
  pause: "Bearbeitung pausiert",
  block: "Als blockiert markiert",
  unblock: "Blockierung aufgehoben",
};

const WARNED_ACTIONS = new Set();

function formatAction(action) {
  const label = ACTION_LABELS[action];
  if (!label) {
    if (!WARNED_ACTIONS.has(action)) {
      // eslint-disable-next-line no-console
      console.warn(`[TaskActivitySection] Unknown action string: ${action}`);
      WARNED_ACTIONS.add(action);
    }
    return action;
  }
  return label;
}


function actorDisplay(item) {
  if (item?.actor_user_full_name && item.actor_user_full_name.trim()) {
    return item.actor_user_full_name.trim();
  }
  if (item?.actor_user_email && item.actor_user_email.trim()) {
    return item.actor_user_email.trim();
  }
  return "Unbekannt";
}


// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

function ActivityRow({ item, isLast }) {
  return (
    <article
      className={
        "flex items-start gap-2 py-3 " +
        (isLast ? "" : "border-b border-border/60 dark:border-[#1e2130]/60")
      }
    >
      <span
        aria-hidden
        className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground dark:bg-[#94a3b8]"
      />
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[13px] text-foreground dark:text-[#c5cbe0]">
          <span className="font-semibold">{formatAction(item.action)}</span>
          <span className="text-muted-foreground dark:text-[#4a5070]">
            {" "}
            — von{" "}
          </span>
          <span>{actorDisplay(item)}</span>
        </p>
        <p className="m-0 mt-0.5 text-[11px] tabular-nums text-muted-foreground dark:text-[#4a5070]">
          {formatDateTime(item.created_at)}
        </p>
      </div>
    </article>
  );
}


// ──────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────

export default function TaskActivitySection({ taskId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // { message, status }
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    if (!taskId) return Promise.resolve();
    const id = ++requestIdRef.current;
    setLoading(true);
    setLoadError(null);
    return fetchAdminTaskActivity(taskId)
      .then((data) => {
        if (id !== requestIdRef.current) return; // stale → discard
        setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((err) => {
        if (id !== requestIdRef.current) return; // stale → discard
        const status = err?.status;
        const message =
          err?.message || "Aktivität konnte nicht geladen werden.";
        setLoadError({ message, status });
        // 404 stays inline — parent already handles task-level 404. Any
        // other failure ALSO toasts so the user sees backend detail.
        if (status !== 404) {
          toast.error(message);
        }
      })
      .finally(() => {
        if (id !== requestIdRef.current) return; // stale → don't clear loading
        setLoading(false);
      });
  }, [taskId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  // ── Loading / error states ────────────────────────────────────────
  if (loading && items.length === 0 && !loadError) {
    return (
      <p
        role="status"
        className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]"
      >
        Lade Aktivität …
      </p>
    );
  }

  if (loadError) {
    return (
      <div
        role="alert"
        className="rounded-[8px] border border-dashed border-destructive/40 bg-destructive/10 p-4 dark:border-[#3a1f24] dark:bg-[#1e0d10]"
      >
        <p className="m-0 text-[13px] text-destructive dark:text-[#ff6b6b]">
          Aufgabe oder Aktivität konnte nicht geladen werden.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="mt-3 min-h-[40px] rounded-[8px] border border-border bg-transparent px-3 py-2 text-[12px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:border-[#1e2130] dark:text-[#94a3b8] dark:hover:bg-[#13161f]"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  // ── Normal render ─────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <p className="m-0 text-[12px] text-muted-foreground dark:text-[#4a5070]">
        Noch keine Aktivität.
      </p>
    );
  }

  return (
    <div>
      {items.map((it, i) => (
        <ActivityRow
          key={it.id}
          item={it}
          isLast={i === items.length - 1}
        />
      ))}
    </div>
  );
}
